<template>
  <AdminShell
    page-title="推广总账单"
    page-subtitle="汇总查看当前商户下所有推广账单，支持按状态筛选和继续支付。"
    active-menu="promotion"
  >
    <template #actions>
      <el-button plain @click="goToList">返回推广列表</el-button>
    </template>

    <section class="promotion-total-bills-page">
      <article class="toolbar-card">
        <el-select v-model="filters.status" clearable placeholder="全部状态" @change="fetchOrders">
          <el-option
            v-for="item in statusOptions"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
        <el-button icon="el-icon-refresh" :loading="loading" @click="fetchOrders">刷新</el-button>
      </article>

      <div class="summary-grid">
        <article class="summary-card">
          <span>账单总数</span>
          <strong>{{ summaryValue('total_orders') }}</strong>
        </article>
        <article class="summary-card">
          <span>待处理账单</span>
          <strong>{{ summaryValue('open_orders') }}</strong>
        </article>
        <article class="summary-card">
          <span>已支付账单</span>
          <strong>{{ summaryValue('paid_orders') }}</strong>
        </article>
        <article class="summary-card">
          <span>已支付金额</span>
          <strong>{{ paidAmountText }}</strong>
        </article>
      </div>

      <article class="table-card">
        <el-table
          v-loading="loading"
          :data="orders"
          row-key="id"
          empty-text="暂无账单"
        >
          <el-table-column label="推广活动" min-width="220">
            <template slot-scope="{ row }">
              <div class="campaign-cell">
                <strong>{{ row.campaign_name || `推广 #${row.campaign_id}` }}</strong>
                <span>#{{ row.campaign_id }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column label="账单号" width="140">
            <template slot-scope="{ row }">
              <span>#{{ row.id }}</span>
            </template>
          </el-table-column>

          <el-table-column label="预算快照" min-width="170">
            <template slot-scope="{ row }">
              <span>{{ billingSnapshotText(row) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="结算金额" min-width="140">
            <template slot-scope="{ row }">
              <span>{{ formatMoney(row.settlement_amount, row.settlement_currency) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="状态" width="120">
            <template slot-scope="{ row }">
              <span class="status-chip" :class="`is-${row.status}`">{{ paymentStatusLabel(row.status) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="创建时间" width="170">
            <template slot-scope="{ row }">
              <span>{{ formatDateTime(row.created_at) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="260" fixed="right">
            <template slot-scope="{ row }">
              <div class="action-group">
                <el-button type="text" @click="openCampaignBills(row)">账单明细</el-button>
                <el-button
                  v-if="isPayableStatus(row.status)"
                  type="text"
                  @click="goToCheckout(row)"
                >
                  {{ row.status === 'created' ? '去支付' : '继续支付' }}
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </article>
    </section>
  </AdminShell>
</template>

<script>
import { getUserInfo, listPromotionPaymentOrders } from '@/api'
import AdminShell from '@/components/admin/AdminShell.vue'
import { promotionPaymentStatusLabel } from '@/constants/promotion-payment'
import { formatDateTime, formatMoney, isPayableStatus, resolveCommercialId } from '@/utils/promotion-form'

const STATUS_OPTIONS = [
  { value: 'created', label: '待发起支付' },
  { value: 'pending', label: '待支付' },
  { value: 'processing', label: '支付处理中' },
  { value: 'paid', label: '支付成功' },
  { value: 'failed', label: '支付失败' },
  { value: 'cancelled', label: '已取消' },
  { value: 'expired', label: '已过期' },
]

export default {
  name: 'PromotionTotalBillsPage',
  components: {
    AdminShell,
  },
  data() {
    return {
      loading: false,
      commercialId: '',
      orders: [],
      summary: null,
      filters: {
        status: '',
      },
      statusOptions: STATUS_OPTIONS,
    }
  },
  computed: {
    paidAmountText() {
      const currency = this.orders[0]?.settlement_currency || 'USD'
      return formatMoney(this.summary?.total_paid_amount || 0, currency)
    },
  },
  async mounted() {
    await this.initialize()
  },
  methods: {
    async initialize() {
      const { data } = await getUserInfo()
      this.commercialId = String(resolveCommercialId(data) || '')
      await this.fetchOrders()
    },
    async fetchOrders() {
      if (!this.commercialId) return
      this.loading = true
      try {
        const res = await listPromotionPaymentOrders({
          commercial_id: this.commercialId,
          status: this.filters.status || undefined,
          limit: 100,
          offset: 0,
        })
        this.orders = Array.isArray(res?.data?.items) ? res.data.items : []
        this.summary = res?.data?.summary || null
      } catch (error) {
        this.$message.error(error.message || '加载推广总账单失败')
      } finally {
        this.loading = false
      }
    },
    goToList() {
      this.$router.push('/promotion')
    },
    openCampaignBills(row) {
      this.$router.push(`/promotion/${row.campaign_id}/bills`)
    },
    goToCheckout(row) {
      this.$router.push({
        path: '/promotion/checkout',
        query: {
          campaignId: row.campaign_id,
          orderId: row.id,
        },
      })
    },
    summaryValue(key) {
      return this.summary ? Number(this.summary[key] || 0).toLocaleString() : '--'
    },
    billingSnapshotText(row) {
      const snapshot = row.billing_snapshot || {}
      const budgetType = snapshot.budget_type === 'lifetime' ? '总预算' : '单日预算'
      return `${budgetType} ${String(snapshot.currency || row.original_currency || 'USD').toUpperCase()} ${Number(snapshot.budget_amount || row.original_amount || 0).toFixed(2)}`
    },
    paymentStatusLabel: promotionPaymentStatusLabel,
    formatDateTime,
    formatMoney,
    isPayableStatus,
  },
}
</script>

<style scoped>
.promotion-total-bills-page {
  display: grid;
  gap: 18px;
}

.toolbar-card,
.summary-card,
.table-card {
  border: 1px solid #dbe4f0;
  border-radius: 22px;
  background: rgba(255, 255, 255, 0.96);
}

.toolbar-card {
  padding: 18px 20px;
  display: flex;
  align-items: center;
  gap: 12px;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 16px;
}

.summary-card {
  padding: 20px;
  display: grid;
  gap: 10px;
}

.summary-card span,
.campaign-cell span {
  color: #64748b;
  font-size: 13px;
}

.summary-card strong,
.campaign-cell strong {
  color: #0f172a;
}

.campaign-cell {
  display: grid;
  gap: 4px;
}

.table-card {
  overflow: hidden;
}

.action-group {
  display: flex;
  align-items: center;
  gap: 10px;
}

.status-chip {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 76px;
  padding: 6px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
}

.status-chip.is-created,
.status-chip.is-pending {
  color: #9a3412;
  background: #ffedd5;
}

.status-chip.is-processing {
  color: #1d4ed8;
  background: #dbeafe;
}

.status-chip.is-paid {
  color: #166534;
  background: #dcfce7;
}

.status-chip.is-failed,
.status-chip.is-cancelled,
.status-chip.is-expired {
  color: #991b1b;
  background: #fee2e2;
}

@media (max-width: 960px) {
  .summary-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
